'use client';

import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@ui/lib/utils';
import { triggerSparkles } from './sparkles';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-bold ring-offset-background duration-100 active:scale-[0.995] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        default:
          'relative bg-slate-900 text-white font-bold rounded-lg border border-slate-900 px-4 py-2 hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-800 focus:ring-offset-2 disabled:opacity-50 shadow-[0_1px_2px_rgba(0,0,0,0.1),inset_0_1px_0_rgba(255,255,255,0.15)]',
        destructive:
          'bg-destructive text-white border border-destructive hover:bg-destructive/90',
        outline:
          'bg-white text-slate-800 ring-1 ring-inset ring-slate-200/65 shadow-[2px_2px_5px_rgba(0,0,0,0.03),_-2px_-2px_5px_rgba(255,255,255,0.5)] hover:bg-slate-50/80 transition duration-200 ease-in-out focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-slate-300/60 focus-visible:ring-offset-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900',
        secondary:
          'bg-secondary text-secondary-foreground border border-secondary hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        neumorphicLight:
          'bg-white text-slate-800 ring-1 ring-inset ring-slate-200/60 hover:bg-slate-50/80 transition duration-200 ease-in-out',
        neumorphicDark:
          'bg-slate-800 text-white border border-slate-800 hover:bg-slate-700',
        bookNow:
          'bg-gradient-to-r from-red-500 via-red-600 to-rose-600 text-white border border-red-600 rounded-lg w-full transition-all duration-300 relative overflow-hidden hover:shadow-xl',
        PositiveAction:
          'shrink-0 bg-emerald-700 text-white border border-emerald-700 hover:bg-emerald-800 shadow-[0_1px_2px_rgba(0,0,0,0.1),inset_0_1px_0_rgba(255,255,255,0.15)]',
        filled:
          'relative bg-slate-900 text-white font-bold rounded-lg border border-slate-900 px-4 py-2 hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-800 focus:ring-offset-2 disabled:opacity-50 shadow-[0_1px_2px_rgba(0,0,0,0.1),inset_0_1px_0_rgba(255,255,255,0.15)]',
      },
      size: {
        default: 'h-10 px-4 py-2',
        xxs: 'h-5 rounded-lg px-1 text-[11px]',
        xs: 'h-7 rounded-lg px-2 text-xs',
        sm: 'h-9 rounded-lg px-3',
        lg: 'h-11 rounded-xl px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  sparklesOnClick?: boolean;
  isLoading?: boolean;
  sparklesWithLoader?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      iconLeft,
      iconRight,
      sparklesOnClick,
      sparklesWithLoader = false,
      isLoading = false,
      children,
      onClick,
      disabled,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';
    const buttonRef = React.useRef<HTMLButtonElement>(null);

    // Track if sparkles should be shown on next click
    const shouldShowSparkles =
      sparklesOnClick && (!isLoading || sparklesWithLoader);

    // If we have icons but no children, adjust the class
    const hasOnlyIcon = (iconLeft || iconRight) && !children;
    const sizeClass = hasOnlyIcon && size !== 'icon' ? 'icon' : size;

    // Button variant custom shadows
    const buttonStyle =
      variant === 'bookNow'
        ? {
            boxShadow:
              'inset 6px 5px 7px -7px rgba(180, 60, 60, 0.5), inset 5px -1px 4px -3px rgba(255, 68, 68, 0.24), inset -5px -5px 8px -2px rgba(73, 29, 38, 0.1), 0 0 12px -2px rgba(234, 110, 139, 0.15), inset 0 2px 2px rgba(255, 255, 255, 0.17)',
          }
        : {};

    // Handle click with sparkles effect
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      // Only trigger sparkles if appropriate
      if (shouldShowSparkles && !disabled) {
        // Get the button's center position rather than the exact click position
        const rect = e.currentTarget.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;

        // Professional, subtle sparkle effect (Airbnb-quality)
        // Extremely minimal sparkles with perfect placement for a refined feel

        // First create a subtle preparation effect - barely noticeable
        // This creates the impression of intention rather than randomness
        triggerSparkles(centerX, centerY);

        setTimeout(() => {
          // Just a single delicate sparkle slightly offset from center
          const subtleOffset = Math.min(rect.width, rect.height) * 0.04;
          triggerSparkles(centerX - subtleOffset, centerY - subtleOffset * 0.5);
        }, 10);

        // Then add 2-3 perfectly placed sparkles in a minimal, refined pattern
        // Fewer sparkles = more elegant and intentional (less is more)
        setTimeout(() => {
          // Main sparkle - perfectly centered for that premium feel
          triggerSparkles(
            centerX,
            centerY - Math.min(rect.width, rect.height) * 0.15
          );
        }, 60);
      }

      // Call the original onClick if it exists
      if (onClick) {
        onClick(e);
      }
    };

    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size: sizeClass, className }),
          size === 'xxs' ? 'gap-0.5' : size === 'xs' ? 'gap-1' : 'gap-2',
          iconLeft && size === 'xxs' && 'pl-0',
          iconLeft && size === 'xs' && 'pl-1',
          iconLeft && size !== 'xxs' && size !== 'xs' && 'pl-2'
        )}
        ref={(node: HTMLButtonElement | null) => {
          // Handle both the forwarded ref and our internal ref
          if (typeof ref === 'function') {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
          buttonRef.current = node;
        }}
        style={buttonStyle}
        onClick={handleClick}
        disabled={disabled || (isLoading && !sparklesWithLoader)}
        {...props}
      >
        {asChild ? (
          // When asChild is true, only render children (for Slot component compatibility)
          children
        ) : (
          // Normal button rendering with icons
          <>
            {iconLeft && !isLoading && (
              <span
                className={cn(
                  'flex-shrink-0',
                  size === 'xxs' ? 'h-2.5 w-2.5' : 'h-3.5 w-3.5'
                )}
              >
                {iconLeft}
              </span>
            )}
            {children}
            {iconRight && !isLoading && (
              <span
                className={cn(
                  'flex-shrink-0',
                  size === 'xxs' ? 'h-2.5 w-2.5' : 'h-3.5 w-3.5'
                )}
              >
                {iconRight}
              </span>
            )}
          </>
        )}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
